import React, { useEffect, useState } from "react";
import { DialogContainer, Button } from "react-md";
import { Checkbox } from "@material-ui/core";
import crossIcon from "../../../../../assets/icons/close-cross1.png";
import { normalizeLabel, labelKey } from "./categoryLabel";

const ZY_ORANGE = "#F77F11";

// Reusable dialog to pick inclusion / exclusion categories for a claim rule row.
// Controlled by the parent via `visible`. Local checkbox / add-category state is
// (re)seeded each time the dialog transitions to visible so the parent only needs
// to supply the candidate + already-selected label lists.
export default function CategorySelectDialog({
  visible,
  type = "inclusions", // "inclusions" | "exclusions"
  subtitle = "",
  candidates = [],
  initialSelected = [],
  // Universal ("standard") labels that are always selected and cannot be
  // unticked — rendered checked + disabled. A subset of `candidates`. Used for
  // the standard exclusions the claim workflow always enforces.
  lockedLabels = [],
  viewOnly = false,
  onCancel,
  onSave,
  onAddCategory,
}) {
  const [options, setOptions] = useState([]);
  const [checked, setChecked] = useState({});
  const [showAddInput, setShowAddInput] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  useEffect(() => {
    if (visible) {
      // Union of candidates + locked + already-selected (possibly custom) labels,
      // preserving candidate order and appending customs at the end. Dedupe
      // case-insensitively so a saved selection that differs only by case /
      // whitespace collapses onto its candidate rather than showing twice.
      const merged = [...candidates];
      const addMerged = (lbl) => {
        if (!merged.some((m) => labelKey(m) === labelKey(lbl))) merged.push(lbl);
      };
      // Locked labels must always be present even if the candidate catalog is
      // stale, so they can never silently drop out of the standard set.
      (lockedLabels || []).forEach(addMerged);
      (initialSelected || []).forEach(addMerged);
      setOptions(merged);

      // Tick the option that actually renders (the merged entry), which may
      // differ in casing from the raw selected label. Locked labels are always
      // ticked regardless of what was saved.
      const initChecked = {};
      const tick = (lbl) => {
        const match = merged.find((m) => labelKey(m) === labelKey(lbl)) || lbl;
        initChecked[match] = true;
      };
      if ((initialSelected || []).length > 0) {
        // Existing saved selection — reflect exactly what was saved.
        (initialSelected || []).forEach(tick);
      } else {
        // No saved selection yet: global categories apply by default, so pre-check
        // every candidate. The user can uncheck any that don't apply to this plan.
        merged.forEach((lbl) => {
          initChecked[lbl] = true;
        });
      }
      (lockedLabels || []).forEach(tick);
      setChecked(initChecked);

      setShowAddInput(false);
      setNewLabel("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  const title = type === "exclusions" ? "Exclusions" : "Inclusions";

  const isLocked = (label) =>
    (lockedLabels || []).some((l) => labelKey(l) === labelKey(label));

  const toggle = (label) => {
    if (viewOnly || isLocked(label)) return;
    setChecked((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleAdd = () => {
    const label = normalizeLabel(newLabel);
    if (!label) return;
    // Case-insensitive dedup: if a normalized-equal option already exists, just
    // select it instead of creating a near-duplicate candidate.
    const existing = options.find((o) => labelKey(o) === labelKey(label));
    if (existing) {
      setChecked((prev) => ({ ...prev, [existing]: true }));
    } else {
      setOptions((prev) => [...prev, label]);
      setChecked((prev) => ({ ...prev, [label]: true }));
      if (onAddCategory) onAddCategory(label);
    }
    setNewLabel("");
    setShowAddInput(false);
  };

  const handleSave = () => {
    const selectedLabels = options.filter((lbl) => checked[lbl]);
    if (onSave) onSave(selectedLabels);
  };

  return (
    <DialogContainer
      id="category-select-dialog"
      visible={true}
      focusOnMount={false}
      onHide={() => {
        if (onCancel) onCancel();
      }}
      autosizeContent
      height="auto"
      style={{ textAlign: "left" }}
      dialogStyle={{ borderRadius: "10px", position: "relative" }}
      width="560px"
    >
      <div
        style={{
          padding: "15px 20px 12px 20px",
          color: "black",
          fontFamily: "Montserrat",
          fontSize: "14px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ margin: 0 }}>{title}</h3>
            {subtitle ? (
              <div style={{ color: "#777777", marginTop: "4px" }}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <div>
            <Button
              plain
              style={{
                backgroundColor: "transparent",
                boxShadow: "none",
                minWidth: "auto",
                padding: 0,
              }}
              onClick={() => onCancel && onCancel()}
            >
              <img src={crossIcon} alt="close" height="25px" />
            </Button>
          </div>
        </div>

        <div
          style={{
            marginTop: "16px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: "12px",
            rowGap: "2px",
            maxHeight: "320px",
            overflowY: "auto",
          }}
        >
          {options.length === 0 ? (
            <div style={{ gridColumn: "1 / span 2", color: "#999999" }}>
              No categories yet. Use "+ Add category" below.
            </div>
          ) : (
            options.map((label) => {
              const locked = isLocked(label);
              return (
                <div
                  key={label}
                  style={{ display: "flex", alignItems: "center" }}
                  title={
                    locked
                      ? "Standard exclusion — always applies and cannot be removed"
                      : undefined
                  }
                >
                  <Checkbox
                    color="secondary"
                    disabled={viewOnly || locked}
                    checked={!!checked[label]}
                    onChange={() => toggle(label)}
                    style={{ padding: "4px" }}
                  />
                  <span style={locked ? { color: "#666666" } : undefined}>
                    {label}
                    {locked ? (
                      <span
                        style={{
                          marginLeft: "6px",
                          fontSize: "10px",
                          fontWeight: 600,
                          color: "#999999",
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                        }}
                      >
                        Standard
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {!viewOnly &&
          (showAddInput ? (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <input
                type="text"
                value={newLabel}
                autoFocus
                placeholder="Category name"
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  border: "1px solid #C2C2C2",
                  borderRadius: "5px",
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                }}
              />
              <Button
                raised
                style={{
                  backgroundColor: ZY_ORANGE,
                  color: "#FFFFFF",
                  textTransform: "none",
                  borderRadius: "5px",
                }}
                onClick={handleAdd}
              >
                Add
              </Button>
            </div>
          ) : (
            <div
              style={{
                marginTop: "12px",
                color: ZY_ORANGE,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-block",
              }}
              onClick={() => setShowAddInput(true)}
            >
              + Add category
            </div>
          ))}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "24px",
          }}
        >
          <Button
            raised
            style={{
              backgroundColor: "#EBEBEB",
              color: "#333333",
              textTransform: "none",
              borderRadius: "5px",
              minWidth: "110px",
              marginLeft: "10px",
            }}
            onClick={() => onCancel && onCancel()}
          >
            Cancel
          </Button>
          {!viewOnly && (
            <Button
              raised
              style={{
                backgroundColor: ZY_ORANGE,
                color: "#FFFFFF",
                textTransform: "none",
                borderRadius: "5px",
                minWidth: "110px",
                marginLeft: "10px",
              }}
              onClick={handleSave}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </DialogContainer>
  );
}
